/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import {
	AbstractControl,
	UntypedFormControl
} from '@angular/forms';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	PaymentAllocationsComponent
} from '@insurance/components/payment-allocations/payment-allocations.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	IDropdownOption
} from '@shared/interfaces/application-objects/dropdown-option.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	delay,
	from,
	map,
	Observable,
	of,
	Subject,
	switchMap,
	takeUntil
} from 'rxjs';

/* eslint-enable max-len */

@Component({
	selector: 'adjustment-details',
	templateUrl: './adjustment-details.component.html'
})

/**
 * A component representing a wizard step to collect the commission adjustment
 * details information.
 *
 * @export
 * @class AdjustmentDetailsComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class AdjustmentDetailsComponent
implements OnInit,
	OnDestroy,
	IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the adjustment write off component.
	 *
	 * @param {InsuranceService} insuranceService
	 * The insurance service used to lookup insurance modules.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @memberof AdjustmentDetailsComponent
	 */
	public constructor(
		public insuranceService: InsuranceService,
		public siteLayoutService: SiteLayoutService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof AdjustmentDetailsComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the payment allocations child component reference.
	 *
	 * @type {PaymentAllocationsComponent}
	 * @memberof AdjustmentDetailsComponent
	 */
	@ViewChild('paymentAllocations')
	public paymentAllocations: PaymentAllocationsComponent;

	/**
	 * Gets or sets the payment allocation settings retrieved from
	 * the product associated to the policy term.
	 *
	 * @type {any}
	 * @memberof AdjustmentDetailsComponent
	 */
	public paymentAllocationSettings: any;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof AdjustmentDetailsComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the transaction entity instance that
	 * is associated with the commission payment.
	 *
	 * @type {IEntityInstance}
	 * @memberof AdjustmentDetailsComponent
	 */
	public transaction: IEntityInstance;

	/**
	 * Gets or sets the list of commission transactions
	 * associated to the current transaction.
	 *
	 * @type {any[]}
	 * @memberof AdjustmentDetailsComponent
	 */
	public commissionTransactions: any[];

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof AdjustmentDetailsComponent
	 */
	public get currentData(): any
	{
		return this.context.source.activeMenuItem.currentData.data;
	}

	/**
	 * Gets the agency ID from the current context data.
	 *
	 * @type {number}
	 * @memberof AdjustmentDetailsComponent
	 */
	public get agencyId(): number
	{
		return Number(this.currentData.id);
	}

	/**
	 * Gets a value indicating whether payment allocations are enabled.
	 *
	 * @type {boolean}
	 * @memberof AdjustmentDetailsComponent
	 */
	public get paymentAllocationsEnabled(): boolean
	{
		return this.paymentAllocationSettings?.enabled ?? false;
	}

	/**
	 * Gets the reason field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentDetailsComponent
	 */
	public get reasonFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'reason');
	}

	/**
	 * Gets the transaction number field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentDetailsComponent
	 */
	public get transactionNumberFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'transactionNumber');
	}

	/**
	 * Gets the commission transaction field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * The commission transaction field configuration.
	 * @memberof AdjustmentDetailsComponent
	 */
	public get commissionTransactionFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'commissionTransactionId');
	}

	/**
	 * Gets the amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentDetailsComponent
	 */
	public get amountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'amount');
	}

	/**
	 * Gets the transaction amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentDetailsComponent
	 */
	public get transactionAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'transactionAmount');
	}

	/**
	 * Gets the auto allocate button configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentDetailsComponent
	 */
	public get autoAllocateButtonConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'autoAllocateButton');
	}

	/**
	 * Gets the allocation amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentDetailsComponent
	 */
	public get allocationAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'allocationAmount');
	}

	/**
	 * Subject for managing subscription cleanup on component destruction.
	 *
	 * @private
	 * @type {Subject<void>}
	 * @memberof AdjustmentDetailsComponent
	 */
	private readonly destroy: Subject<void> = new Subject<void>();

	/**
	 * Implements the on initialization interface.
	 *
	 * @memberof AdjustmentDetailsComponent
	 */
	public ngOnInit(): void
	{
		this.initializeComponent();
	}

	/**
	 * Implements the on destroy interface to perform cleanup.
	 *
	 * @memberof AdjustmentDetailsComponent
	 */
	public ngOnDestroy(): void
	{
		this.destroy.next();
		this.destroy.complete();
	}

	/**
	 * Performs component initialization.
	 *
	 * @memberof AdjustmentDetailsComponent
	 */
	public initializeComponent()
	{
		this.performPostInitActions();

		this.context.source.addToNext(
			this.next.bind(this));

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Handles the navigation to the next step.
	 *
	 * @memberof AdjustmentDetailsComponent
	 */
	public next(): void
	{
		this.context.source.addOrUpdateStepData(
			{
				policyNumber:
					this.transaction
						?.data
						?.policyNumber,
				transactionId:
					this.transaction?.id
			});
	}

	/**
	 * Validates the transaction number control.
	 *
	 * @param {AbstractControl} control
	 * The form control to get the input value.
	 * @return {Observable<boolean>}
	 * An Observable that resolves to a boolean value
	 * that indicates the transaction number validity
	 * @memberof AdjustmentDetailsComponent
	 */
	public transactionNumberValidator(
		control: AbstractControl): Observable<boolean>
	{
		// This validator triggers with an empty value
		// only of the field is not required.
		if (AnyHelper.isNullOrWhitespace(
			control.value))
		{
			return of(true);
		}

		return of(control.value)
			.pipe(
				delay(
					AppConstants
						.time
						.threeQuarterSecond),
				switchMap((value: any) =>
					from(
						this.insuranceService
							.searchTransaction(
								this.agencyId,
								value))
				),
				map((matchingTransaction: IEntityInstance) =>
				{
					this.transaction = matchingTransaction;

					return !AnyHelper
						.isNull(
							matchingTransaction);
				}));
	}

	/**
	 * Clears the transaction related data from the current step
	 * and the wizard context.
	 *
	 * @memberof AdjustmentDetailsComponent
	 */
	public clearTransactionData(): void
	{
		this.transaction = null;
		this.paymentAllocationSettings = null;

		this.reloadCommissionTransactions();
	}

	/**
	 * Reloads the commission transactions associated
	 * to the current transaction.
	 *
	 * @memberof AdjustmentDetailsComponent
	 */
	public reloadCommissionTransactions(): void
	{
		this.commissionTransactionFieldConfig
			.formControl
			.setValue(null);
		this.commissionTransactionFieldConfig
			.formControl
			.updateValueAndValidity();

		if (this.commissionTransactionFieldConfig
			.props
			.initializeDataOptions)
		{
			this.commissionTransactionFieldConfig
				.props
				.initializeDataOptions();
		}
	}

	/**
	 * Loads the commission transactions associated to the current transaction.
	 *
	 * @async
	 * @return {Promise<any[]>}
	 * The promise containing the list of commission transactions.
	 * @memberof AdjustmentDetailsComponent
	 */
	public async loadCommissionTransactions(): Promise<any[]>
	{
		if (AnyHelper.isNull(this.transaction))
		{
			this.commissionTransactions = [];

			return [];
		}

		this.commissionTransactions =
			await this.insuranceService
				.getAdjustableCommissionsByTransactionId(
					this.agencyId,
					this.transaction.id);

		return this.commissionTransactions;
	}

	/**
	 * Handles the status change of the transaction number control.
	 *
	 * @param {string} status
	 * The status of the formly field.
	 * @memberof AdjustmentDetailsComponent
	 */
	public async handleTransactionNumberStatus(
		status: string): Promise<void>
	{
		if(status
			=== FormlyConstants
				.controlStatus
				.pending)
		{
			return;
		}

		if (status
			=== FormlyConstants
				.controlStatus
				.invalid
			|| AnyHelper.isNullOrEmpty(
				this.transactionNumberFieldConfig
					.formControl
					.value))
		{
			this.clearTransactionData();

			return;
		}

		const product: IEntityInstance =
			await this.insuranceService
				.getProductByTransaction(
					this.transaction);

		this.paymentAllocationSettings =
			product?.data?.billing?.paymentAllocations;

		this.reloadCommissionTransactions();

		this.paymentAllocations.paymentAllocationSettings =
			this.paymentAllocationSettings;
		this.paymentAllocations.transaction =
			this.transaction;
	}

	/**
	 * Handles the commission transaction field change event.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration.
	 * @memberof AdjustmentDetailsComponent
	 */
	public onCommissionTransactionChange(
		field: FormlyFieldConfig): void
	{
		const commissionTransaction: any =
			this.findCommissionTransaction(
				Number(field.formControl.value));

		this.paymentAllocations
			?.setCommissionTransaction(
				commissionTransaction);
		this.amountFieldConfig
			.formControl
			.updateValueAndValidity();
	}

	/**
	 * Handles the validity changed event for the dynamic formly component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @async
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof AdjustmentDetailsComponent
	 */
	public async validityChanged(
		isValid: boolean): Promise<void>
	{
		await this.context.source.validStepChanged(
			isValid
				&& (!this.paymentAllocationsEnabled
					|| this.paymentAllocations?.valid));
	}

	/**
	 * Handles the validity changed event for the payment allocations component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @async
	 * @param {boolean} isValid
	 * The validity of the payment allocations component.
	 * @memberof AdjustmentDetailsComponent
	 */
	public async paymentAllocationsValidityChanged(
		isValid: boolean): Promise<void>
	{
		await this.context.source.validStepChanged(
			isValid
				&& this.reasonFieldConfig
					.formControl
					.parent
					.valid);
	}

	/**
	 * Gets the description to be shown for a
	 * commission transaction in the dropdown.
	 *
	 * @param {any} item
	 * The commission transaction item.
	 * @return {string}
	 * The description of the commission transaction.
	 * @memberof AdjustmentDetailsComponent
	 */
	public getCommissionTransactionDescription(
		item: any): string
	{
		return item.data.note
			+ ' - '
			+ StringHelper.format(
				item.data.amount,
				AppConstants.formatTypes.currency);
	}

	/**
	 * Finds the commission transaction object
	 * from the list of commission transactions
	 * based on the given commission transaction id.
	 *
	 * @param {number} commissionTransactionId
	 * The commission transaction ID.
	 * @return {any}
	 * The commission transaction object.
	 * @memberof AdjustmentDetailsComponent
	 */
	public findCommissionTransaction(
		commissionTransactionId: number): any
	{
		return this.commissionTransactions
			?.find(
				(item: any) =>
					item.id === commissionTransactionId);
	}

	/**
	 * Gets a value indicating whether the auto allocate button
	 * should be hidden.
	 *
	 * @return {boolean}
	 * A value indicating whether the auto allocate button should be hidden.
	 * @memberof AdjustmentDetailsComponent
	 */
	public isAutoAllocateButtonHidden(): boolean
	{
		return !this.paymentAllocationsEnabled
			|| this.reasonFieldConfig
				.formControl
				.value
				!== InsuranceConstants
					.commissionAdjustmentReasonTypes
					.recoupment;
	}

	/**
	 * Handles the post initialization action.
	 * This will create the dynamic formly layout for display component creation
	 * and permissions.
	 *
	 * @memberof AdjustmentDetailsComponent
	 */
	private performPostInitActions(): void
	{
		const allocationFieldsExpressions: any =
			{
				hide: () =>
					!this.paymentAllocationsEnabled
			};

		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				<FormlyFieldConfig>
				{
					key: 'reason',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						placeholder: 'Select an Option',
						label: 'Reason',
						required: true,
						default: 'Correction',
						options:
							<IDropdownOption[]>
							[
								{
									label:
										InsuranceConstants
											.commissionAdjustmentReasonTypes
											.correction,
									value:
										InsuranceConstants
											.commissionAdjustmentReasonTypes
											.correction
								},
								{
									label:
										InsuranceConstants
											.commissionAdjustmentReasonTypes
											.recoupment,
									value:
										InsuranceConstants
											.commissionAdjustmentReasonTypes
											.recoupment
								},
								{
									label:
										InsuranceConstants
											.commissionAdjustmentReasonTypes
											.other,
									value:
										InsuranceConstants
											.commissionAdjustmentReasonTypes
											.other
								}
							]
					}
				},
				{
					key: 'transactionNumber',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						type: 'string',
						label: 'Transaction Id',
						tooltip:
							'Transaction Id can be found in the Transaction '
								+ 'table displayed in the Policy Term Summary '
								+ 'page'
					},
					expressions: {
						'props.required': () =>
							this.reasonFieldConfig
								.formControl
								.value
									=== InsuranceConstants
										.commissionAdjustmentReasonTypes
										.correction
					},
					hooks: {
						onInit: (field) => {
							field
								.formControl
								.statusChanges
								.pipe(takeUntil(this.destroy))
								.subscribe(
									status => {
										this.handleTransactionNumberStatus(
											status);
									});
						},
					},
					asyncValidators: {
						transactionNumberValidator: {
							expression: (
								control: UntypedFormControl,
								_: FormlyFieldConfig) =>
								this.transactionNumberValidator(
									control),
							message:
								'This transaction number does not exist.'
						}
					}
				},
				{
					key: 'commissionTransactionId',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Type',
						placeholder: 'Select an Option',
						showClear: true,
						dataPromise:
							this.loadCommissionTransactions.bind(this),
						labelFunction:
							this.getCommissionTransactionDescription.bind(this),
						valueTemplate: '${item.id}',
						type: 'number',
						change:
							this.onCommissionTransactionChange.bind(this),
						disableAutoSetValue: true
					},
					expressions: {
						'props.required': () =>
							!AnyHelper.isNullOrEmpty(
								this.transactionNumberFieldConfig
									.formControl
									.value)
							&& this.reasonFieldConfig
								.formControl
								.value
									=== InsuranceConstants
										.commissionAdjustmentReasonTypes
										.correction
					}
				},
				{
					key: 'amount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Amount',
						useCurrency: true,
						required: true
					},
					validators: {
						validAmount: {
							expression: (control: UntypedFormControl) =>
								control.value !== 0,
							message: 'Invalid amount value.'
						}
					}
				},
				{
					key: 'transactionDate',
					type: FormlyConstants.customControls.customCalendar,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Bank Transaction Date'
					}
				},
				{
					key: 'transactionAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Transaction Amount',
						useCurrency: true,
						disabled: true
					},
					expressions:
						allocationFieldsExpressions
				},
				{
					wrappers: [
						FormlyConstants.customControls.customEmptyWrapper
					],
					props: {
						gridColumns: 6
					},
					expressions: {
						hide: () =>
							!this.isAutoAllocateButtonHidden()
					}
				},
				{
					key: 'autoAllocateButton',
					type: FormlyConstants.customControls.customOperationButton,
					wrappers: [],
					props: {
						label: 'Auto Allocate',
						hideLabel: true,
						gridColumns: 6
					},
					expressions: {
						hide: () =>
							this.isAutoAllocateButtonHidden()
					}
				},
				{
					key: 'allocationAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Allocation Amount',
						useCurrency: true,
						disabled: true
					},
					expressions:
						allocationFieldsExpressions
				}
			];
	}
}